import { CopyOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Drawer, Input, Modal, PageHeader, Space, Typography } from 'antd';
import React from 'react';
import { DrawerWidth } from '../../config/dimensions';
import { ActionOption, ModuleName } from '../../core/models/enum';
import { isUserAllowed } from '../../helpers/CheckPermissionHelper';
import { errorWithUserMessage, success } from '../../helpers/NotificationHelper';
import { ApiTokenCreatedVm } from '../../utils/api';
import ApiTokenForm from './api-token-form/ApiTokenForm';
import ApiTokenTable from './api-token-table';
import { Props } from './index';

interface State {
    drawerVisible: boolean;
    createdToken?: string;
    isUserAllowedToCreate: boolean;
}

class ApiTokens extends React.PureComponent<Props, State> {
    private tableRef: any;

    public constructor(props: Props) {
        super(props);

        this.tableRef = React.createRef();

        this.state = {
            drawerVisible: false,
            isUserAllowedToCreate: false,
        };
    }

    public componentDidMount = (): void => {
        const { userProfile } = this.props;

        this.setState({
            isUserAllowedToCreate: isUserAllowed(
                userProfile,
                ModuleName.ApiTokens,
                ActionOption.Create
            ),
        });
    };

    private openDrawer = (): void => {
        this.setState({ drawerVisible: true });
    };

    private closeDrawer = (): void => {
        this.setState({ drawerVisible: false });
    };

    private handleCreated = (result: ApiTokenCreatedVm): void => {
        // Close the form, show the token once, and reload the list.
        this.setState({
            drawerVisible: false,
            createdToken: result.token,
        });

        if (this.tableRef.current) {
            this.tableRef.current.refresh();
        }
    };

    private closeTokenModal = (): void => {
        this.setState({ createdToken: undefined });
    };

    private copyToken = (): void => {
        const { createdToken } = this.state;
        if (!createdToken) {
            return;
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
                .writeText(createdToken)
                .then((): void => success('Token copied to clipboard.'))
                .catch((): void => errorWithUserMessage('Could not copy. Please copy manually.'));
        } else {
            errorWithUserMessage('Clipboard not available. Please copy manually.');
        }
    };

    public render(): React.ReactElement {
        const { userProfile } = this.props;
        const { drawerVisible, createdToken, isUserAllowedToCreate } = this.state;

        return (
            <div>
                <PageHeader
                    title="API Tokens"
                    extra={
                        isUserAllowedToCreate
                            ? [
                                  <Button
                                      key="1"
                                      type="primary"
                                      style={{ zIndex: 10, float: 'right' }}
                                      onClick={this.openDrawer}
                                  >
                                      <PlusOutlined className="button-icon-display" />
                                      Create token
                                  </Button>,
                              ]
                            : []
                    }
                    style={{ padding: '16px 0px' }}
                />

                <ApiTokenTable userProfile={userProfile} wrappedComponentRef={this.tableRef} />

                <Drawer
                    title="Create API token"
                    visible={drawerVisible}
                    onClose={this.closeDrawer}
                    width={DrawerWidth}
                    destroyOnClose
                >
                    <ApiTokenForm onCreated={this.handleCreated} onCancel={this.closeDrawer} />
                </Drawer>

                <Modal
                    title="Token created"
                    visible={!!createdToken}
                    onCancel={this.closeTokenModal}
                    footer={[
                        <Button
                            key="copy"
                            type="primary"
                            icon={<CopyOutlined />}
                            onClick={this.copyToken}
                        >
                            Copy
                        </Button>,
                        <Button key="done" onClick={this.closeTokenModal}>
                            Done
                        </Button>,
                    ]}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Alert
                            type="warning"
                            showIcon
                            message="Copy this token now."
                            description="For security reasons it is shown only once and cannot be retrieved again. If you lose it, revoke it and create a new one."
                        />
                        <Typography.Text type="secondary">Token</Typography.Text>
                        <Input.TextArea
                            value={createdToken}
                            readOnly
                            autoSize={{ minRows: 4, maxRows: 8 }}
                        />
                    </Space>
                </Modal>
            </div>
        );
    }
}

export default ApiTokens;
